import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  Input,
  Select,
  DatePicker,
  AutoComplete,
  Switch,
  Tooltip,
} from "antd";
import moment from "moment";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import {
  fetchCustomers,
  fetchAllCustomers,
  fetchCustomerSubmissionInfo,
} from "../../../actions/dashboard";
import { getUserList } from "../../../actions/sendReports";
import { getUser } from "../../../utils/helpers";
import CocWoTabs from "./CocWoTabs";
import CustomerSubmissionStats from "./CustomerSubmissionStats";
import "./Customers.css";
import "./DashboardShared.css";

const Customers = () => {
  const columnWidth = "50px";
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchValue, setSearchValue] = useState("");
  const [csm, setCsm] = useState(null);
  const [csmUsers, setCsmUsers] = useState([]);
  const [csmLoading, setCsmLoading] = useState(true);
  const [dataOpsCsm, setDataOpsCsm] = useState(null);
  const [dataOpsCsmUsers, setDataOpsCsmUsers] = useState([]);
  const [clickedCustomer, setClickedCustomerState] = useState(null);
  const clickedCustomerRef = useRef(null);
  const setClickedCustomer = (nextCustomerId) => {
    clickedCustomerRef.current = nextCustomerId;
    setClickedCustomerState(nextCustomerId);
  };
  const [cocWoDateRange, setCocWoDateRange] = useState(null);
  const [filterRetest, setFilterRetest] = useState(false);
  const [cocWoSearchParams, setCocWoSearchParams] = useState({
    paramName: "sample_id",
    value: "",
  });
  const [customerOptions, setCustomerOptions] = useState([]);
  const [, setApiController] = useState(null);
  const [isMounted, setIsMounted] = useState(true); // Track mounted state
  const [productEnabled, setProductEnabled] = useState(false);
  const [environmentEnabled, setEnvironmentEnabled] = useState(false);
  const [productTotalSamples, setProductTotalSamples] = useState(null);
  const [environmentTotalSamples, setEnvironmentTotalSamples] = useState(null);
  const [esvSubmissions, setEsvSubmissions] = useState(null);
  const [otherSubmissions, setOtherSubmissions] = useState(null);
  /** From submission-info API `esv`; null until loaded for current filters */
  const [customerOnboardedEsv, setCustomerOnboardedEsv] = useState(null);
  const submissionInfoAbortRef = useRef(null);
  const customerTableAreaRef = useRef(null);
  const [customerTableScrollY, setCustomerTableScrollY] = useState(undefined);
  const [statsLoading, setStatsLoading] = useState(false);
  const [paginationConfig, setPaginationConfig] = useState({
    current: 1,
    pageSize: 100,
    total: 10,
    pageSizeOptions: ["10", "50", "100"],
    isPageChange: false,
  });
  /** MM/DD/YYYY or "-" — same idea as CustomerSubmissionDetailModal date lines. */
  const formatCustomerTableDate = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    const m = moment(value);
    return m.isValid() ? m.format("MM/DD/YYYY") : "-";
  };

  const parseOptionalNumber = (value) => {
    if (value === null || value === undefined || value === "") return null;
    if (typeof value === "number" && !Number.isNaN(value)) return value;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const parsePermissionEnabled = (value) => {
    if (value === false || value === "false" || value === 0 || value === "0") return false;
    return value === true || value === "true" || value === 1 || value === "1";
  };

  const columns = [
    {
      title: "Customer #", dataIndex: "customer_id", key: "customer_id", width: columnWidth,
    },
    {
      title: "Company", dataIndex: "company", key: "company", width: columnWidth,
    },
    {
      title: "CSM", dataIndex: "csm_name", key: "csm_name", width: columnWidth,
    },
    {
      title: "Data Ops", dataIndex: "data_ops_csm_name", key: "data_ops_csm_name", width: columnWidth,
    },
    {
      title: "Onboarding",
      dataIndex: "created_at",
      key: "onboarded",
      width: columnWidth,
      render: (_, record) => {
        const onboardedDisplay = formatCustomerTableDate(record.created_at);
        const firstSampleRaw = record.first_sample_created_at;
        const firstSampleDisplay = formatCustomerTableDate(firstSampleRaw);
        const showOnboardedTip = onboardedDisplay !== "-";
        const showFirstSampleTip = firstSampleDisplay !== "-";
        const content = (
          <div className="Dashboard__OnboardedCell">
            <div>
              <span className="Dashboard__OnboardedCell__Label">Onboarded:</span>
              {" "}
              {onboardedDisplay}
            </div>
            <div>
              <span className="Dashboard__OnboardedCell__Label">First Sample:</span>
              {" "}
              {firstSampleDisplay}
            </div>
          </div>
        );
        if (showOnboardedTip || showFirstSampleTip) {
          return (
            <Tooltip
              placement="top"
              title={(
                <div>
                  {showOnboardedTip && (
                    <>
                      <span>
                        Onboarded:
                        {" "}
                        {onboardedDisplay}
                      </span>
                      {showFirstSampleTip ? <br /> : null}
                    </>
                  )}
                  {showFirstSampleTip && (
                    <span>
                      First Sample:
                      {" "}
                      {firstSampleDisplay}
                    </span>
                  )}
                </div>
              )}
            >
              <span className="Dashboard__OnboardedCellWrapper">{content}</span>
            </Tooltip>
          );
        }
        return content;
      },
    },
  ];

  const CustomerTableRow = ({
    record, index, onRowClick, ...props
  }) => {
    const className = !record ? "" : `Dashboard__TableRow${clickedCustomer === record.id ? " TableRow_HightLightRow" : ""}`;
    return (
    <tr {...props} className={className} onClick={() => onRowClick ? onRowClick(record) : null} /> /* eslint-disable-line */
    );
  };

  const CustomerTableCell = ({
    children, dataIndex, record, ...restProps
  }) => <td {...restProps}>{children}</td>; // eslint-disable-line react/jsx-props-no-spreading

  const abortPrevApiCall = (controller) => {
    setApiController((prevController) => {
      if (prevController) prevController.abort();
      return controller;
    });
  };

  /** Last 3 months through today — same default as on first customer selection. */
  const getDefaultCocWoDateRange = () => [dayjs().subtract(3, "month"), dayjs()];

  const formatDateRange = () => {
    if (!cocWoDateRange) {
      return "";
    }
    const [start, end] = cocWoDateRange;
    return `${moment(start.$d).format("MM/DD/YYYY")}-${moment(end.$d).format("MM/DD/YYYY")}`;
  };

  /**
   * Make an API call to fetch customers information when
   * 1. Landing the page
   * 2. On Search enter
   * 3. On CSM Name change
   * 4. On Page change
   */
  const fetchCustomersApi = async () => {
    setLoading(true);
    const { current, pageSize, isPageChange: prevIsPageChange } = paginationConfig;
    const currPage = prevIsPageChange ? current : 1; // on page change, we use current, otherwise set the current page to 1
    const parameters = {
      page: currPage,
      page_size: pageSize,
      search: searchValue,
      csm,
      data_ops_csm: dataOpsCsm,
      status: 1, // 1: Active, 0: Inactive. Fetch only active customers
    };
    const controller = new AbortController();
    abortPrevApiCall(controller);
    const {
      success, results, message, count, canceled,
    } = await fetchCustomers(parameters, controller.signal);
    if (!isMounted) return;
    if (success) {
      setCustomers(results.map((item, rowIndex) => ({
        ...item,
        key: rowIndex,
        contact_details: `${item.telephone} ${item.email}`,
      })));
      setPaginationConfig((prevState) => { // Set the current page and reset the isPageChange to false to prevent the useEffect call dependent on paginationConfig
        const newState = {
          ...prevState, current: currPage, total: count, isPageChange: false,
        };
        return newState;
      });
    } else if (!canceled) {
      setCustomers([]);
      setPaginationConfig((prevState) => {
        const newState = {
          ...prevState, current: 1, total: 10, isPageChange: false,
        };
        return newState;
      });
      toast.error(message);
    }
    setClickedCustomer(null);
    setCocWoDateRange(null);
    setLoading(canceled);
  };

  /**
   * This useEffect handle the on table page changing event.
   * If user clicks any page number on the table footer, the isPageChange will be set to true,
   * so it will trigger the fetchCustomersApi() to fetch the results based on the latest page number
   */
  useEffect(() => {
    if (paginationConfig && paginationConfig.isPageChange) {
      fetchCustomersApi();
    }
  }, [paginationConfig]); // eslint-disable-line

  const handlePageChange = (page, pageSize) => {
    setPaginationConfig((prevState) => ({
      ...prevState,
      current: page,
      pageSize,
      isPageChange: true, // set this key to true to state that this is a page change event
    }));
  };

  /**
   * Make an API call to fetch all active CSM(role 4) and CSM Lead (role 10) users
   */
  const fetchUsersApi = async () => {
    setCsmLoading(true);
    const {
      success, results, canceled,
    } = await getUserList();
    if (!isMounted) return;
    const unassigned = { value: -1, label: "Unassigned" };
    const csmList = [unassigned];
    const dataOpsCsmList = [unassigned];
    if (success) {
      const user = getUser();
      results.forEach((item) => {
        const {
          id, first_name, last_name, role,
        } = item;
        const options = { value: id, label: `${first_name} ${last_name}`.trim() };

        // Set up the CSM users list. 4: CSM, 10: CSM Lead
        if ([4, 10].includes(role)) {
          if (id === user.id) { // if the current logged in user was in the dropdown list, then set the default value to the logged in user's name
            setCsm(id);
          }
          csmList.push(options);
        } else if ([20, 22, 27].includes(role)) { // 20: Data Ops, 22: Data Ops Lead, 27: Data Ops Intermediate
          // Set up the Data Ops CSM users list
          if (id === user.id) { // if the current logged in user was in the dropdown list, then set the default value to the logged in user's name
            setDataOpsCsm(id);
          }
          dataOpsCsmList.push(options);
        }
      });
      setCsmUsers(csmList);
      setDataOpsCsmUsers(dataOpsCsmList);
    } else if (!canceled) {
      setCsmUsers(csmList);
      setDataOpsCsmUsers(dataOpsCsmList);
    }
    setCsmLoading(canceled);
  };

  /**
   * Fetch all customers information at once to display the search dropdown options
   */
  const fetchCustomerOptionsApi = async () => {
    const { success, list } = await fetchAllCustomers();
    if (success) {
      setCustomerOptions(list.map((item) => ({
        value: `${item.company} (${item.customer_id})`,
        customerId: item.customer_id,
      })));
    }
  };

  /**
   * This useEffect will do the following on landing
   * 1. Fetch CSM users
   * 2. Fetch all customers information for the search dropdown
   * 3. Set the isMounted to false when the component is unmounted
   */
  useEffect(() => {
    fetchUsersApi();
    fetchCustomerOptionsApi();
    return () => {
      setIsMounted(false);
    };
  }, []); // eslint-disable-line

  /** Fit table body scroll inside the flex panel so pagination stays visible. */
  useEffect(() => {
    const tableArea = customerTableAreaRef.current;
    if (!tableArea) return undefined;

    const updateTableScrollY = () => {
      const tableHead = tableArea.querySelector(".ant-table-thead");
      const pagination = tableArea.querySelector(".ant-table-pagination");
      const overhead = (tableHead?.offsetHeight ?? 55) + (pagination?.offsetHeight ?? 48);
      const nextScrollY = Math.max(120, tableArea.clientHeight - overhead);
      setCustomerTableScrollY((prev) => (prev === nextScrollY ? prev : nextScrollY));
    };

    updateTableScrollY();
    const resizeObserver = new ResizeObserver(updateTableScrollY);
    resizeObserver.observe(tableArea);
    return () => resizeObserver.disconnect();
  }, [loading, customers.length, paginationConfig.total]);

  /**
   * This useEffect will fetch the customers table data when searchValue, csm user, or the csmLoading is onChange
   */
  useEffect(() => {
    if (!csmLoading) { // Landing will be set to false after fetching all the CSM users
      fetchCustomersApi();
    }
  }, [searchValue, csm, dataOpsCsm, csmLoading]); // eslint-disable-line

  /**
   * Customer submission analytics (samples, permissions, ESV) — GET /customer-submission-info
   */
  const fetchCustomerSubmissionInfoApi = async (payload) => {
    const requestCustomerId = payload.customer_id;

    if (submissionInfoAbortRef.current) {
      submissionInfoAbortRef.current.abort();
    }
    const controller = new AbortController();
    submissionInfoAbortRef.current = controller;

    setStatsLoading(true);
    try {
      const {
        success,
        canceled,
        message: submissionInfoMessage,
        esv,
        product_permission_enabled,
        environment_permission_enabled,
        product_total_samples,
        environment_total_samples,
        submissions_info,
      } = await fetchCustomerSubmissionInfo(payload, controller.signal);
      if (!isMounted) return;
      if (canceled) return;
      if (clickedCustomerRef.current !== requestCustomerId) return;
      if (success) {
        const esvOnboarded = esv === true || esv === "true" || esv === 1;
        setCustomerOnboardedEsv(esvOnboarded);
        setProductEnabled(parsePermissionEnabled(product_permission_enabled));
        setEnvironmentEnabled(parsePermissionEnabled(environment_permission_enabled));
        setProductTotalSamples(parseOptionalNumber(product_total_samples));
        setEnvironmentTotalSamples(parseOptionalNumber(environment_total_samples));
        const submissions = submissions_info != null ? submissions_info : {};
        const othersParsed = parseOptionalNumber(
          submissions.others_submissions != null
            ? submissions.others_submissions
            : submissions.other_submissions,
        );
        setEsvSubmissions(parseOptionalNumber(submissions.esv_submissions));
        setOtherSubmissions(othersParsed);
      } else if (!canceled) {
        setCustomerOnboardedEsv(null);
        setProductTotalSamples(null);
        setEnvironmentTotalSamples(null);
        setEsvSubmissions(null);
        setOtherSubmissions(null);
        if (submissionInfoMessage) {
          toast.error(submissionInfoMessage);
        }
      }
    } catch (error) {
      toast.error("An error occurred while fetching customer submission info.");
    } finally {
      if (isMounted) {
        setStatsLoading(false);
      }
    }
  };

  /**
   * Clear selection when the clicked row is no longer in the current table data
   * (e.g. after filter/page refresh) so we never pass an empty customer into Spec Manager.
   */
  useEffect(() => {
    if (!clickedCustomer || loading) return;
    const isSelectedRowInTable = customers.some((row) => row.id === clickedCustomer);
    if (!isSelectedRowInTable) {
      if (submissionInfoAbortRef.current) {
        submissionInfoAbortRef.current.abort();
      }
      setCustomerOnboardedEsv(null);
      setClickedCustomer(null);
    }
  }, [customers, clickedCustomer, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * When the selected customer changes, reset stats until GET /customer_submission_info returns
   * (product/environment permissions come from that response).
   */
  useEffect(() => {
    if (clickedCustomer) {
      setProductEnabled(false);
      setEnvironmentEnabled(false);
      setProductTotalSamples(null);
      setEnvironmentTotalSamples(null);
      setEsvSubmissions(null);
      setOtherSubmissions(null);
      setCustomerOnboardedEsv(null);
    } else {
      // Reset all stats when no customer is selected
      setProductEnabled(false);
      setEnvironmentEnabled(false);
      setProductTotalSamples(null);
      setEnvironmentTotalSamples(null);
      setEsvSubmissions(null);
      setOtherSubmissions(null);
      setCustomerOnboardedEsv(null);
    }
  }, [clickedCustomer]); // eslint-disable-line

  /**
   * Fetches submission info when date range, filters, or customer change
   */
  useEffect(() => {
    if (clickedCustomer && cocWoDateRange) {
      const dateRange = formatDateRange();
      fetchCustomerSubmissionInfoApi({
        customer_id: clickedCustomer,
        date_range: dateRange,
        sample_id: cocWoSearchParams.paramName === "sample_id" ? cocWoSearchParams.value : "",
        sample_type: cocWoSearchParams.paramName === "sample_type" ? cocWoSearchParams.value : "",
        retest: filterRetest,
      });
    } else {
      setProductTotalSamples(null);
      setEnvironmentTotalSamples(null);
      setEsvSubmissions(null);
      setOtherSubmissions(null);
      setCustomerOnboardedEsv(null);
    }
    // eslint-disable-next-line
  }, [
    cocWoDateRange, clickedCustomer, filterRetest, cocWoSearchParams.paramName, cocWoSearchParams.value,
  ]);

  const handleSearchOnChange = (e) => {
    if (e.target.value === "") {
      setSearchValue(e.target.value);
    }
  };

  const handleSearch = (value) => {
    setSearchValue(value);
  };

  const onRowClick = (row) => {
    const { id } = row;
    const nextCustomerId = id === clickedCustomer ? null : id;
    if (nextCustomerId !== clickedCustomer) {
      setCustomerOnboardedEsv(null);
    }
    setClickedCustomer(nextCustomerId);
    setCocWoSearchParams({
      value: "",
      paramName: "sample_id",
    });
  };

  const handleDateChange = (dates) => {
    setCocWoDateRange(dates);
  };

  /**
   * Set default date range to last 3 months when a customer is selected
   */
  useEffect(() => {
    if (clickedCustomer && !cocWoDateRange) {
      setCocWoDateRange(getDefaultCocWoDateRange());
    }
  }, [clickedCustomer]); // eslint-disable-line
  const handleCocWoSearchTypeChange = (value) => {
    setCocWoSearchParams((prev) => ({
      ...prev,
      paramName: value,
    }));
  };

  // const formatDateRange = () => {
  //   if (!cocWoDateRange) {
  //     return "";
  //   }
  // }

  const handleEnterKeyPress = (e) => {
    e.preventDefault();
    setCocWoSearchParams((prev) => ({
      ...prev,
      value: e.target.value,
    }));
  };

  const handleCocWoSearchOnchange = (e) => {
    if (!e.target.value) {
      setCocWoSearchParams((prev) => ({
        ...prev,
        value: "",
      }));
    }
  };

  const getCocWoSearchPlaceholder = () => {
    if (cocWoSearchParams.paramName === "sample_id") {
      return "Search by Sample ID";
    }
    return "Search by Sample Type";
  };

  const selectedCustomer = clickedCustomer
    ? customers.find((customerRow) => customerRow.id === clickedCustomer)
    : null;

  return (
    <div className="Dashboard__Customers">
      <div className="Dashboard__CustomersDiv">
        <div className="Dashboard__CustomersHeader">
          <AutoComplete
            className="CustomersHeader__AutoComplete"
            popupClassName="CustomersHeader__AutoCompletePopup"
            options={customerOptions}
            filterOption={(inputValue, option) => option.value?.toLowerCase().startsWith(inputValue?.toLowerCase()) || option.customerId?.toLowerCase().startsWith(inputValue?.toLowerCase())}
            onSelect={(_, option) => setSearchValue(option.customerId)}
          >
            <Input.Search
              className="CustomersHeader__SearchFilter"
              placeholder="Customer, Company"
              allowClear
              onPressEnter={(e) => setSearchValue(e.target.value)}
              onSearch={handleSearch}
              onChange={handleSearchOnChange}
            />
          </AutoComplete>
          <Select
            className="CustomersHeader__CSMFilter"
            showSearch
            allowClear
            placeholder="CSM"
            value={csm}
            loading={csmLoading}
            options={csmUsers}
            filterOption={(inputValue, option) => option.label?.toLowerCase().startsWith(inputValue?.toLowerCase())}
            onChange={(csmId) => setCsm(csmId)}
          />
          <Select
            className="CustomersHeader__CSMFilter"
            showSearch
            allowClear
            placeholder="Data Ops"
            value={dataOpsCsm}
            loading={csmLoading}
            options={dataOpsCsmUsers}
            filterOption={(inputValue, option) => option.label?.toLowerCase().startsWith(inputValue?.toLowerCase())}
            onChange={(userId) => setDataOpsCsm(userId)}
          />
        </div>
        <div className="Dashboard__CustomersTableArea" ref={customerTableAreaRef}>
          <Table
            loading={loading}
            scroll={customerTableScrollY ? { y: customerTableScrollY } : undefined}
            pagination={{ ...paginationConfig, onChange: handlePageChange }}
            components={{
              body: {
                row: CustomerTableRow,
                cell: CustomerTableCell,
              },
            }}
            onRow={(record, index) => ({ record, index, onRowClick })}
            columns={columns.map((col) => ({
              ...col,
              onCell: (record) => ({
                record,
                dataIndex: col.dataIndex,
              }),
            }))}
            dataSource={customers}
          />
        </div>
      </div>
      <div className="Dashboard__CocWoDiv">
        {!selectedCustomer && <div className="DashBoard__NoResults">Please select a Customer</div>}
        {selectedCustomer && (
        <div className="Dashboard__DetailHeader">
          <div className="Dashboard__DetailHeaderRow">
            <DatePicker.RangePicker
              allowClear={false}
              format="MM/DD/YYYY"
              className="Dashboard__Date"
              value={cocWoDateRange}
              onChange={handleDateChange}
            />
            <div className="Dashboard__FilterRetestToggle">
              <div className="Dashboard__FilterRetestLabel">Filter Retest</div>
              <Switch
                checked={filterRetest}
                onChange={setFilterRetest}
              />
            </div>
            <div className="Dashboard__CustomersHeader">
              <Input
                type="search"
                className="Dashboard__CocWoSearch CustomersHeader__SearchFilter"
                placeholder={getCocWoSearchPlaceholder()}
                allowClear
                onPressEnter={(e) => handleEnterKeyPress(e)}
                onChange={(e) => handleCocWoSearchOnchange(e)}
                addonAfter={(
                  <Select
                    className="Dashboard__CocWoSearchTypeFilter"
                    value={cocWoSearchParams.paramName}
                    options={[
                      { value: "sample_type", label: "Sample Type" },
                      { value: "sample_id", label: "Sample ID" },
                    ]}
                    onChange={handleCocWoSearchTypeChange}
                  />
                )}
              />
            </div>
          </div>

          <CustomerSubmissionStats
            customerOnboardedEsv={customerOnboardedEsv}
            productPermissionEnabled={productEnabled}
            environmentPermissionEnabled={environmentEnabled}
            productTotalSamples={productTotalSamples}
            environmentTotalSamples={environmentTotalSamples}
            esvSubmissionsCount={esvSubmissions}
            otherSubmissionsCount={otherSubmissions}
            statsLoading={statsLoading}
            hasDateRangeSelected={Boolean(cocWoDateRange)}
          />
        </div>
        )}

        {selectedCustomer && (
          <CocWoTabs
            customerId={selectedCustomer.id}
            customerAccountId={selectedCustomer.customer_id}
            customerName={selectedCustomer.company}
            dateRange={formatDateRange()}
            filterRetest={filterRetest}
            searchParams={cocWoSearchParams}
            isCustomerEsvOnboarded={customerOnboardedEsv}
          />
        )}
      </div>
    </div>
  );
};

export default Customers;
